/**
 * Virtual Pet Game
 * Care for your pet by managing hunger, health, and happiness
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const VirtualPet = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('virtualpet_score', 0);
  const [highScore, setHighScore] = useLocalStorage('virtualpet_highScore', 0);

  const [health, setHealth] = useState(100);
  const [happiness, setHappiness] = useState(100);
  const [hunger, setHunger] = useState(30);
  const [energy, setEnergy] = useState(80);
  const [age, setAge] = useState(0);
  const [petName, setPetName] = useState('Buddy');
  const [mood, setMood] = useState('happy');
  const [inputName, setInputName] = useState('Buddy');

  const startGame = () => {
    setHealth(100);
    setHappiness(100);
    setHunger(30);
    setEnergy(80);
    setAge(0);
    setScore(0);
    setPetName(inputName);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (age > highScore) setHighScore(age);
    setGameState(GAME_CONFIG.states.MENU);
  };

  const feed = () => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;
    setHunger(Math.max(0, hunger - 30));
    setHealth(Math.min(100, health + 5));
    setScore((prev) => prev + 10);
  };

  const play = () => {
    if (gameState !== GAME_CONFIG.states.PLAYING || energy < 20) return;
    setHappiness(Math.min(100, happiness + 25));
    setEnergy(Math.max(0, energy - 20));
    setHunger(Math.min(100, hunger + 10));
    setScore((prev) => prev + 15);
  };

  const sleep = () => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;
    setEnergy(Math.min(100, energy + 40));
    setHealth(Math.min(100, health + 10));
    setHunger(Math.min(100, hunger + 5));
    setScore((prev) => prev + 5);
  };

  const pet = () => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;
    setHappiness(Math.min(100, happiness + 15));
    setScore((prev) => prev + 5);
  };

  // Update pet stats over time
  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const timer = setInterval(() => {
      setAge((prev) => prev + 1);
      setHunger((prev) => Math.min(100, prev + 1));
      setEnergy((prev) => Math.max(0, prev - 0.5));
      setHappiness((prev) => Math.max(0, prev - 0.3));
      setHealth((prev) => Math.max(0, prev - 0.2));
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState]);

  // Determine pet mood and check if game over
  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    let newMood = 'happy';
    if (health < 30) newMood = 'sick';
    else if (hunger > 80) newMood = 'starving';
    else if (happiness < 30) newMood = 'sad';
    else if (energy < 20) newMood = 'tired';

    setMood(newMood);

    if (health <= 0) {
      alert(`${petName} has passed away! Final age: ${Math.floor(age)} seconds`);
      resetGame();
    }
  }, [health, hunger, happiness, energy, gameState, age, petName]);

  const getMoodEmoji = () => {
    const moods = {
      happy: '😊',
      sad: '😢',
      sick: '🤒',
      starving: '😫',
      tired: '😴',
    };
    return moods[mood] || '😊';
  };

  return (
    <GameLayout
      title="Virtual Pet 🐾"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Virtual Pet</h2>
            <p style={styles.menuDescription}>
              Name your pet and care for it. Keep it healthy and happy!
            </p>
            <input
              type="text"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              placeholder="Enter pet name"
              style={styles.nameInput}
            />
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <div style={styles.playingArea}>
            <h3 style={{ color: GAME_CONFIG.colors.light }}>{petName}</h3>
            <div style={styles.petDisplay}>
              <div style={styles.petEmoji}>{getMoodEmoji()} 🐶</div>
              <div style={styles.petAge}>Age: {Math.floor(age)}s</div>
            </div>

            <div style={styles.statsContainer}>
              <div style={styles.stat}>
                <label>Health</label>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.max(0, health)}%`,
                      backgroundColor: health > 60 ? GAME_CONFIG.colors.success : health > 30 ? '#ffb347' : GAME_CONFIG.colors.danger,
                    }}
                  />
                </div>
                <span>{Math.floor(health)}</span>
              </div>

              <div style={styles.stat}>
                <label>Happiness</label>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.max(0, happiness)}%`,
                      backgroundColor: GAME_CONFIG.colors.accent2,
                    }}
                  />
                </div>
                <span>{Math.floor(happiness)}</span>
              </div>

              <div style={styles.stat}>
                <label>Hunger</label>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.max(0, hunger)}%`,
                      backgroundColor: hunger < 50 ? GAME_CONFIG.colors.success : hunger < 80 ? '#ffb347' : GAME_CONFIG.colors.danger,
                    }}
                  />
                </div>
                <span>{Math.floor(hunger)}</span>
              </div>

              <div style={styles.stat}>
                <label>Energy</label>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${Math.max(0, energy)}%`,
                      backgroundColor: GAME_CONFIG.colors.primary,
                    }}
                  />
                </div>
                <span>{Math.floor(energy)}</span>
              </div>
            </div>

            <div style={styles.actionContainer}>
              <button
                onClick={feed}
                className="btn btn-primary"
                disabled={hunger === 0}
              >
                🍖 Feed
              </button>
              <button
                onClick={play}
                className="btn btn-success"
                disabled={energy < 20}
              >
                🎾 Play
              </button>
              <button
                onClick={sleep}
                className="btn btn-warning"
              >
                😴 Sleep
              </button>
              <button
                onClick={pet}
                className="btn btn-info"
              >
                🤚 Pet
              </button>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
    minWidth: '400px',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '1.5rem',
  },
  nameInput: {
    padding: '0.5rem',
    width: '100%',
    borderRadius: '4px',
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    marginBottom: '1rem',
  },
  playingArea: {
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    minWidth: '450px',
    color: GAME_CONFIG.colors.light,
  },
  petDisplay: {
    textAlign: 'center',
    marginBottom: '2rem',
    padding: '1rem',
    backgroundColor: `rgba(102, 126, 234, 0.2)`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  petEmoji: {
    fontSize: '5rem',
    marginBottom: '0.5rem',
  },
  petAge: {
    fontSize: '1.2rem',
    color: GAME_CONFIG.colors.accent,
  },
  statsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  progressBar: {
    width: '100%',
    height: '24px',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderRadius: '4px',
    overflow: 'hidden',
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    color: '#fff',
  },
  actionContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    justifyContent: 'center',
  },
};

export default VirtualPet;
